import type { CausalFilterDto, ProfileConfigSnapshot } from '../models/impact.types'

import { toFiniteNumber, inclusiveDayCount } from './causalNumbers'
import type { CausalEffect, MetricCatalog, MetricDelta } from './causalOutputMapper'

export interface ScopeFilterChip {
  field: string
  operator: string
  values: string[]
}

export interface ScopePresentation {
  targetMetric: string | null
  controlMetrics: string[]
  filters: ScopeFilterChip[]
  preDays: number | null
  postDays: number | null
  minClicks: number | null
  minDaysSinceChange: number | null
  minActiveDaysBefore: number | null
  minActiveDaysAfter: number | null
  maxCredits: number | null
}

const OPERATOR_SYMBOLS: Readonly<Record<string, string>> = {
  equals: '=',
  not_equals: '!=',
  in: '=',
  not_in: '!=',
  contains: '~',
  not_contains: '!~',
  greater_than: '>',
  greater_than_or_equal_to: '>=',
  less_than: '<',
  less_than_or_equal_to: '<=',
}

function valuesOf(value: unknown): string[] {
  const values = Array.isArray(value) ? value : [value]
  return values.filter((entry) => entry != null).map(String)
}

export function filterChip(filter: CausalFilterDto): ScopeFilterChip {
  const operator = filter.operator.toLowerCase()
  return {
    field: filter.field,
    operator: OPERATOR_SYMBOLS[operator] ?? operator,
    values: valuesOf(filter.value),
  }
}

/** Pure config presentation; components own i18n, metric labels and locale formatting. */
export function presentAnalysisScope(snapshot: ProfileConfigSnapshot): ScopePresentation {
  const fanout = snapshot.fanout
  const targetControls = fanout?.target_controls
  const filters = [
    ...(snapshot.change_source?.filters ?? []),
    ...(snapshot.data_source?.filters ?? []),
  ]
  return {
    targetMetric: targetControls?.target_metric ?? null,
    controlMetrics: targetControls?.control_metrics ?? [],
    filters: filters.map(filterChip),
    preDays: fanout?.pre_days ?? null,
    postDays: fanout?.post_days ?? null,
    minClicks: fanout?.min_clicks_total ?? null,
    minDaysSinceChange: fanout?.min_days_after_intervention ?? null,
    minActiveDaysBefore: fanout?.min_unique_days ?? null,
    minActiveDaysAfter: fanout?.min_post_unique_days ?? null,
    maxCredits: snapshot.max_credits ?? null,
  }
}

/** `flat` is v3's amber "no measurable move"; `neutral` is a metric with no known direction. */
export type DeltaTone = 'positive' | 'negative' | 'flat' | 'neutral'

/**
 * v3 `getDeltaColor` as a tone instead of a Tailwind class: the sign is good or bad depending on
 * the metric's direction, and a metric the catalog does not classify stays neutral.
 */
export function deltaTone(
  metricKey: string,
  value: number | string | MetricDelta | null | undefined,
  catalog: MetricCatalog<unknown>,
): DeltaTone {
  const raw = typeof value === 'object' && value !== null ? value.delta : value
  const numeric = toFiniteNumber(raw)
  if (numeric === null || numeric === 0) return 'flat'

  const lowerIsBetter = catalog.lowerIsBetter(metricKey)
  if (lowerIsBetter === true) return numeric < 0 ? 'positive' : 'negative'
  if (lowerIsBetter === false) return numeric > 0 ? 'positive' : 'negative'
  return 'neutral'
}

export interface SideMetricRow<TColumn = unknown> {
  metricKey: string
  label: string
  column: TColumn | null
  preAvg: number
  postAvg: number
  delta: number
  /** Target metric that had no baseline: the view renders the NEW badge instead of a delta. */
  isNew: boolean
  tone: DeltaTone
}

/**
 * The "what changed" rows of the detail drawer. A side metric with a zero baseline is dropped —
 * its percent change would be a meaningless 0 — but the target metric is always kept and flagged
 * `isNew` instead, because a metric that only exists after the change is the finding.
 */
export function sideMetricRows<TColumn>(
  effect: CausalEffect<TColumn>,
  catalog: MetricCatalog<TColumn>,
): SideMetricRow<TColumn>[] {
  return Object.entries(effect.deltas)
    .filter(([metricKey, delta]) => metricKey === effect.targetMetric || delta.preAvg !== 0)
    .map(([metricKey, delta]) => ({
      metricKey,
      label: catalog.metricLabel(metricKey),
      column: catalog.getColumn(metricKey),
      preAvg: delta.preAvg,
      postAvg: delta.postAvg,
      delta: delta.delta,
      isNew: metricKey === effect.targetMetric && delta.preAvg === 0,
      tone: deltaTone(metricKey, delta, catalog),
    }))
}

/** Rendered above the results table; the view formats the two dates and passes them to `t()`. */
export const SCAN_SUMMARY_KEY = 'impactAnalysis.results.scannedSummary'

export interface ScanSummary {
  key: typeof SCAN_SUMMARY_KEY
  params: {
    rangeStart: string | null
    rangeEnd: string | null
    count: number
    level: string
    daysBefore: number | null
    daysAfter: number | null
  }
}

/**
 * "We scanned … and found N {level}. For each, we compared X days before to Y days after."
 * The windows are the first row's, exactly as in v3 — every row of one run shares them.
 */
export function scanSummary(effects: readonly CausalEffect<unknown>[], level: string): ScanSummary {
  const first = effects[0] ?? null
  return {
    key: SCAN_SUMMARY_KEY,
    params: {
      rangeStart: first?.prePeriod?.[0] ?? null,
      rangeEnd: first?.postPeriod?.[1] ?? null,
      count: effects.length,
      level,
      daysBefore: inclusiveDayCount(first?.prePeriod?.[0], first?.prePeriod?.[1]),
      daysAfter: inclusiveDayCount(first?.postPeriod?.[0], first?.postPeriod?.[1]),
    },
  }
}
